import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import apiService from '../services/api';
import '../styles/ReferralTools.css';

const ReferralTools = () => {
  const [referralData, setReferralData] = useState({
    code: '',
    link: '',
    qrDataURL: ''
  });
  const [copySuccess, setCopySuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalClicks: 0,
    todayClicks: 0,
    weekClicks: 0,
    conversions: 0
  });

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [statsResult, analyticsResult] = await Promise.all([
        apiService.dashboard.getStats(),
        apiService.dashboard.getAnalytics()
      ]);

      if (statsResult.success && statsResult.data.referralCode) {
        const code = statsResult.data.referralCode;
        const link = `https://api.u-single2.com/api/referral/ref/${code}`;
        
        // Generate QR code
        const qrDataURL = await QRCode.toDataURL(link, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });

        setReferralData({
          code,
          link,
          qrDataURL
        });
      }

      if (analyticsResult.success) {
        setAnalytics(analyticsResult.data);
      }

    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralData.link);
      setCopySuccess('link');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralData.code);
      setCopySuccess('code');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy code: ', err);
    }
  };

  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.download = `referral-qr-${referralData.code}.png`;
    link.href = referralData.qrDataURL;
    link.click();
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(
      apiService.utils.generateShareContent(referralData.link, 'whatsapp')
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleShareTelegram = () => {
    const message = encodeURIComponent(
      apiService.utils.generateShareContent(referralData.link, 'telegram')
    );
    window.open(`https://t.me/share/url?url=${referralData.link}&text=${message}`, '_blank');
  };

  const handleShareTwitter = () => {
    const message = encodeURIComponent(
      apiService.utils.generateShareContent(referralData.link, 'twitter')
    );
    window.open(`https://twitter.com/intent/tweet?text=${message}`, '_blank');
  };

  const handleShareLinkedIn = () => {
    const url = encodeURIComponent(referralData.link);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const handleShareEmail = () => {
    const emailContent = apiService.utils.generateShareContent(referralData.link, 'email');
    const subject = encodeURIComponent(emailContent.subject);
    const body = encodeURIComponent(emailContent.body);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const refreshAnalytics = async () => {
    try {
      const result = await apiService.dashboard.getAnalytics();
      if (result.success) {
        setAnalytics(result.data);
      }
    } catch (error) {
      console.error('Failed to refresh analytics:', error);
    }
  };

  if (loading) {
    return (
      <div className="referral-tools-content">
        <div className="loading-container">
          <div className="loading-spinner-large"></div>
          <p>Loading referral tools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-tools-content">
      {/* Analytics Cards */}
      <div className="analytics-section">
        <div className="analytics-header">
          <h3>📊 Your Referral Analytics</h3>
          <button className="refresh-btn" onClick={refreshAnalytics}>
            🔄 Refresh
          </button>
        </div>
        <div className="analytics-grid">
          <div className="analytics-card">
            <div className="analytics-value">{analytics.totalClicks.toLocaleString()}</div>
            <div className="analytics-label">Total Clicks</div>
          </div>
          <div className="analytics-card">
            <div className="analytics-value">{analytics.todayClicks.toLocaleString()}</div>
            <div className="analytics-label">Today's Clicks</div>
          </div>
          <div className="analytics-card">
            <div className="analytics-value">{analytics.weekClicks.toLocaleString()}</div>
            <div className="analytics-label">This Week</div>
          </div>
          <div className="analytics-card">
            <div className="analytics-value">{analytics.conversions.toLocaleString()}</div>
            <div className="analytics-label">Conversions</div>
          </div>
        </div>
      </div>

      {/* Main Referral Card */}
      <div className="referral-card">
        <div className="referral-left">
          <h2 className="referral-title">Your Referral Tools</h2>
          <p className="referral-description">
            Share your referral link or QR code to generate referrals and earn commissions.
          </p>
          
          {/* Referral Code */}
          <div className="referral-section">
            <label className="referral-section-label">Referral Code</label>
            <div className="referral-input-group">
              <input 
                type="text" 
                value={referralData.code} 
                readOnly 
                className="referral-input"
              />
              <button 
                className={`copy-input-btn ${copySuccess === 'code' ? 'copied' : ''}`}
                onClick={handleCopyCode}
                title="Copy referral code"
              >
                {copySuccess === 'code' ? '✓' : '📋'}
              </button>
            </div>
          </div>

          {/* Referral Link */}
          <div className="referral-section">
            <label className="referral-section-label">Referral Link</label>
            <div className="referral-input-group">
              <input 
                type="text" 
                value={referralData.link} 
                readOnly 
                className="referral-input"
              />
              <button 
                className={`copy-input-btn ${copySuccess === 'link' ? 'copied' : ''}`}
                onClick={handleCopyLink}
                title="Copy referral link"
              >
                {copySuccess === 'link' ? '✓' : '📋'}
              </button>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="referral-actions">
            <button className="download-btn" onClick={handleDownloadQR}>
              📥 Download QR Code
            </button>
            <button className="share-btn" onClick={handleCopyLink}>
              🔗 Copy Link
            </button>
          </div>

          {/* Social Share Buttons */}
          <div className="social-share-section">
            <h3 className="social-share-title">📱 Share on Social Media</h3>
            <div className="social-share-buttons">
              <button className="social-btn whatsapp" onClick={handleShareWhatsApp}>
                📱 WhatsApp
              </button>
              <button className="social-btn telegram" onClick={handleShareTelegram}>
                ✈️ Telegram
              </button>
              <button className="social-btn twitter" onClick={handleShareTwitter}>
                🐦 Twitter
              </button>
              <button className="social-btn linkedin" onClick={handleShareLinkedIn}>
                💼 LinkedIn
              </button>
              <button className="social-btn email" onClick={handleShareEmail}>
                📧 Email
              </button>
            </div>
          </div>
        </div>
        
        <div className="referral-right">
          <div className="qr-code-container">
            <div className="qr-code-display">
              {referralData.qrDataURL && (
                <img 
                  src={referralData.qrDataURL} 
                  alt="Referral QR Code" 
                  className="qr-code-image"
                />
              )}
            </div>
            <div className="qr-label">
              <div className="scan-text">SCAN ME</div>
              <div className="affiliate-code">{referralData.code}</div>
            </div>
            <button className="qr-download-btn" onClick={handleDownloadQR}>
              Download QR Code
            </button>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="tips-section">
        <h3 className="tips-title">💡 Referral Success Tips</h3>
        <div className="tips-grid">
          <div className="tip-card">
            <div className="tip-icon">🎯</div>
            <h4>Target the Right Audience</h4>
            <p>Share with people interested in affiliate marketing and earning opportunities.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">📱</div>
            <h4>Use Multiple Channels</h4>
            <p>Share on social media, messaging apps, and email to maximize reach.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">💬</div>
            <h4>Personalize Your Message</h4>
            <p>Add a personal touch to explain why you recommend USingle2.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">📊</div>
            <h4>Track Your Performance</h4>
            <p>Monitor your clicks and conversions to optimize your referral strategy.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">⏰</div>
            <h4>Timing Matters</h4>
            <p>Share during peak hours when your audience is most active online.</p>
          </div>
          <div className="tip-card">
            <div className="tip-icon">🏆</div>
            <h4>Be Consistent</h4>
            <p>Regular sharing and engagement leads to better long-term results.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralTools;